import axios, { AxiosInstance } from 'axios';
import { IntermindError } from '../common/models/IntermindError';
import { intermindConfig } from '../config';
import { ListFilterOperator, ListFilterValue, ListParams, X_BYPASS_ERROR_HANDLER } from '../index';

export class HttpClientFactory {
  static create(): AxiosInstance {
    const instance = axios.create({
      baseURL: `${intermindConfig.get('apiPath')}`,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      timeout: 60000,
    });

    this.addAuthorizationInterceptor(instance);
    this.addErrorInterceptor(instance);
    this.addExtraHeadersRequestInterceptor(instance);
    this.addListParamsInterceptor(instance);

    return instance;
  }

  private static addAuthorizationInterceptor(httpClient: AxiosInstance) {
    httpClient.interceptors.request.use(async (config) => {
      const url = config.url ?? '';

      // Allow anonymous to authorize directly in app
      if (url.startsWith('/auth/anonymous')) {
        return config;
      }

      const token = await intermindConfig.get('apiToken')();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = 'Bearer ' + token;
      }

      return config;
    });
  }

  private static addErrorInterceptor(httpClient: AxiosInstance) {
    httpClient.interceptors.response.use(
      (response) => response,
      (error) => {
        const intermindError = new IntermindError(error);

        const bypassErrorHandler = error.config?.headers?.[X_BYPASS_ERROR_HANDLER] === 'true';

        if (intermindConfig.get('onError') && !bypassErrorHandler) {
          intermindConfig.get('onError')!(intermindError);
          return Promise.resolve({ data: null });
        }

        return Promise.reject(intermindError);
      }
    );
  }

  private static addExtraHeadersRequestInterceptor(httpClient: AxiosInstance) {
    httpClient.interceptors.request.use(async (config) => {
      if (intermindConfig.get('customRequestHeaders')) {
        const headers = await intermindConfig.get('customRequestHeaders')?.();
        Object.entries(headers ?? {}).forEach(([key, value]) => {
          config.headers[key] = value;
        });
      }

      // Add Accept-Language header from config
      const locale = intermindConfig.get('locale');
      if (locale) {
        config.headers['Accept-Language'] = locale;
      }

      if (config.headers) {
        Object.entries(config.headers).forEach(([key, value]) => {
          if (value !== undefined) {
            config.headers[key] = value;
          }
        });
      }

      return config;
    });
  }

  private static addListParamsInterceptor(httpClient: AxiosInstance) {
    httpClient.interceptors.request.use((config) => {
      if (config.params && ('filter' in config.params || 'sort' in config.params)) {
        config.params = this.serializeListParams(config.params);
      }
      return config;
    });
  }

  private static serializeListParams(params: ListParams): Record<string, ListFilterValue | ListFilterValue[]> {
    const queryParams: Record<string, ListFilterValue | ListFilterValue[]> = {};

    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.offset !== undefined) queryParams.offset = params.offset;
    if (params.sort !== undefined) {
      queryParams.sort = `${params.sort.fieldPath}:${params.sort.direction}`;
    }

    params.filters?.forEach((filter) => {
      if (!filter.fieldPath || !filter.values || filter.values.length === 0) {
        return; // Skip invalid filters
      }

      const key =
        filter.operator === ListFilterOperator.EQUALS ? filter.fieldPath : `${filter.fieldPath}[${filter.operator}]`;

      // For multiple values, join them with commas
      queryParams[key] = filter.values.length === 1 ? filter.values[0] : filter.values.join(',');
    });

    return queryParams;
  }
}
